import React from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import SpecsRevisionService, {
  extractSpecsValidationResult,
} from "../../service/SpecsRevisionService";
import { useSelector } from "../../common/hooks/useSelector";
import { specsEditorActions } from "../../common/slices/specsEditor/slice";
import type {
  SpecsHistoryRevision,
  SpecsRevisionRecord,
} from "../../service/SpecsRevisionService";
import type { SpecsEditorModalsApi } from "./SpecsEditorModals";
import { formatSpecsRevisionDiff } from "./specsRevisionDiff";
import { resolveImportedVersionName } from "./specsEditorUtils";
import { validateSpecsModel } from "./specsValidationAdapter";
import { filterDisplayedIssues } from "./specsValidationIssues";
import { SpecsEditorContext, type SpecsEditorApi } from "./useSpecsEditor";
import { SpecsModel } from "./specsModel/SpecsModel";
import type { SpecsModelSnapshot } from "./specsModel/types";
import {
  unifyProductImageDimensions,
  uploadNewGrayscaleAssets,
  uploadRGBAAssets,
} from "./specsProductGrayscaleUploads";
import { formatPromotionSuccessMessage } from "./specsRevisionPromotion";

const HISTORY_PAGE_SIZE = 20;

export type RevisionDiffState = {
  status: "idle" | "loading" | "ready" | "error";
  text?: string;
  error?: string;
};

type SaveRevisionResult = {
  revisionId: string;
};

type ProductSnapshot = Record<string, any> & {
  versions: Record<string, any>[];
};

type UseSpecsRevisionHistoryArgs = {
  editor: SpecsEditorApi;
  modals: SpecsEditorModalsApi;
};

export type SpecsRevisionHistoryValue = {
  loadedRevisionId: string | null;
  historySaving: boolean;
  historyPublishing: boolean;
  historyValidating: boolean;
  historyPageProps: {
    productName: string;
    productVersion: string;
    revisions: SpecsHistoryRevision[];
    revisionDiffs: Record<string, RevisionDiffState>;
    loading: boolean;
    error: string | null;
    pageIndex: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
    loadedRevisionId: string | null;
    loadingRevisionId: string | null;
    publishingRevisionId: string | null;
    onPreviousPage: () => void;
    onNextPage: () => void;
    onLoadRevision: (revisionId: string) => Promise<void>;
    onPublishRevision: (revisionId: string) => Promise<void>;
    onExpandRevision: (
      revision: SpecsHistoryRevision,
      fallbackRevisionId: string | null,
      compareAgainstLegacySpecs: boolean
    ) => Promise<void>;
  };
  handleSaveDraftRevision: () => Promise<void>;
  handleSaveAndPublishRevision: () => Promise<void>;
  handleValidateRevision: () => Promise<void>;
};

export function useSpecsRevisionHistory(): SpecsRevisionHistoryValue;
export function useSpecsRevisionHistory(
  args: UseSpecsRevisionHistoryArgs
): SpecsRevisionHistoryValue;
export function useSpecsRevisionHistory(args?: UseSpecsRevisionHistoryArgs) {
  const context = React.useContext(SpecsEditorContext);
  const editor = args?.editor ?? context?.editor ?? null;
  const modals = args?.modals ?? context?.modals ?? null;
  const contextRevisionHistory = context?.revisionHistory ?? null;
  const resolvedEditor = editor as SpecsEditorApi;
  const contextEditor = (context?.editor ?? resolvedEditor) as SpecsEditorApi;
  const resolvedModals = modals as SpecsEditorModalsApi;
  const dispatch = useDispatch();

  const {
    session,
    identity,
    importSpecs,
    selectedPanelKind,
    selectHistory,
    selectPositions,
    specsModel,
    importLoadedRevision,
  } = resolvedEditor;
  const currentUser = useSelector((state) => state.userInfo) as any;
  const [loadedRevisionId, setLoadedRevisionId] = React.useState<string | null>(
    null
  );
  const [historyRevisions, setHistoryRevisions] = React.useState<
    SpecsHistoryRevision[]
  >([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [historyError, setHistoryError] = React.useState<string | null>(null);
  const [historyNextCursor, setHistoryNextCursor] = React.useState<
    string | null
  >(null);
  const [historyCurrentDraftRevisionId, setHistoryCurrentDraftRevisionId] =
    React.useState<string | null>(null);
  const [historyPageIndex, setHistoryPageIndex] = React.useState(0);
  const [historyCursorStack, setHistoryCursorStack] = React.useState<
    Array<string | null>
  >([null]);
  const [historyLoadingRevisionId, setHistoryLoadingRevisionId] =
    React.useState<string | null>(null);
  const [historySaving, setHistorySaving] = React.useState(false);
  const [historyPublishing, setHistoryPublishing] = React.useState(false);
  const [historyValidating, setHistoryValidating] = React.useState(false);
  const [historyPublishingRevisionId, setHistoryPublishingRevisionId] =
    React.useState<string | null>(null);
  const [historyRevisionDiffs, setHistoryRevisionDiffs] = React.useState<
    Record<string, RevisionDiffState>
  >({});
  const [historyRevisionCache, setHistoryRevisionCache] = React.useState<
    Record<string, SpecsRevisionRecord>
  >({});
  const [historyLegacySnapshotCache, setHistoryLegacySnapshotCache] =
    React.useState<Record<string, Record<string, any>>>({});

  const currentHistoryCursor = historyCursorStack[historyPageIndex] ?? null;

  const saveCurrentRevision = React.useCallback(
    async ({
      changeNote,
      sourceRevisionId,
    }: {
      changeNote: string;
      sourceRevisionId: string | null;
    }): Promise<SaveRevisionResult> => {
      if (!specsModel) {
        throw new Error("Unable to save revision without a live specs model");
      }

      const normalizedAssets = await uploadNewGrayscaleAssets({
        model: specsModel,
        specName: identity.productName,
      });
      const normalizedRGBAAssets = await uploadRGBAAssets({
        snapshot: normalizedAssets.snapshot,
        model: specsModel,
        specName: identity.productName,
      });
      const unifiedImageDimensions = await unifyProductImageDimensions({
        snapshot: normalizedRGBAAssets.snapshot,
        specName: identity.productName,
      });
      const productSnapshot =
        unifiedImageDimensions.snapshot as SpecsModelSnapshot;
      const response = await SpecsRevisionService.saveSpecRevision({
        specType: "product",
        specName: identity.productName,
        snapshot: productSnapshot,
        sourceRevisionId,
        createdBy: currentUser?.email || currentUser?.id || "unknown",
        changeNote,
      });

      contextEditor.setImportSelectedProduct(identity.productName);
      contextEditor.setImportSelectedVersion(identity.productVersion);
      if (
        normalizedAssets.uploadedCount > 0 ||
        normalizedRGBAAssets.uploadedCount > 0
      ) {
        contextEditor.setSpecsModel(
          SpecsModel.fromSnapshot(productSnapshot).setValidationContext({
            fabrics: importSpecs?.fabrics,
          })
        );
      }

      return {
        revisionId: response.data?.revisionId as string,
      };
    },
    [
      currentUser?.email,
      currentUser?.id,
      contextEditor,
      identity.productVersion,
      identity.productName,
      importSpecs,
      specsModel,
    ]
  );

  const runValidation = React.useCallback(async () => {
    if (
      !importSpecs ||
      !identity.productName ||
      !identity.productVersion ||
      !specsModel
    ) {
      return false;
    }
    try {
      const result = await validateSpecsModel(importSpecs, specsModel);
      resolvedModals.showValidationResult(result);
      return !!result.valid;
    } catch (error) {
      resolvedModals.showValidationResult({
        valid: false,
        issues: [
          {
            validator: "adapter",
            message: error instanceof Error ? error.message : String(error),
            details: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined,
            path: "adapter",
          },
        ],
      });
      return false;
    }
  }, [
    identity.productName,
    identity.productVersion,
    importSpecs,
    resolvedModals,
    specsModel,
  ]);

  const loadRevisionHistoryPage = React.useCallback(
    async (cursor: string | null) => {
      if (!identity.productName) {
        setHistoryRevisions([]);
        setHistoryNextCursor(null);
        setHistoryError(null);
        return;
      }

      setHistoryLoading(true);
      setHistoryError(null);
      try {
        const response = await SpecsRevisionService.listSpecRevisions(
          "product",
          identity.productName,
          {
            limit: HISTORY_PAGE_SIZE,
            cursor,
          }
        );
        setHistoryRevisions(response.data.revisions ?? []);
        setHistoryNextCursor(response.data.nextCursor ?? null);
        setHistoryCurrentDraftRevisionId(
          response.data.currentDraftRevisionId ?? null
        );
      } catch (error) {
        setHistoryError(
          error instanceof Error ? error.message : "Failed to load revisions"
        );
      } finally {
        setHistoryLoading(false);
      }
    },
    [identity.productName]
  );

  const handleHistoryPreviousPage = React.useCallback(() => {
    setHistoryPageIndex((current) => Math.max(current - 1, 0));
  }, []);

  const handleHistoryNextPage = React.useCallback(() => {
    if (!historyNextCursor) {
      return;
    }

    setHistoryCursorStack((current) => {
      const nextStack = current.slice(0, historyPageIndex + 1);
      nextStack.push(historyNextCursor);
      return nextStack;
    });
    setHistoryPageIndex((current) => current + 1);
  }, [historyNextCursor, historyPageIndex]);

  const handleLoadHistoryRevision = React.useCallback(
    async (revisionId: string) => {
      const productName = identity.productName;
      const preferredVersion = identity.productVersion;

      if (!productName || !preferredVersion) {
        toast.error("Select a product before loading a revision");
        return;
      }

      setHistoryLoadingRevisionId(revisionId);
      try {
        const response = await SpecsRevisionService.getSpecRevisionRuntimeSpecs(
          "product",
          productName,
          revisionId
        );
        const runtimeSpecs = response.data.specs;
        const versionName = resolveImportedVersionName(
          runtimeSpecs,
          productName,
          preferredVersion
        );
        const model = SpecsModel.fromCatalog(
          runtimeSpecs,
          productName,
          versionName
        );
        const version = model?.getVersion(versionName) ?? null;

        if (!model || !version) {
          throw new Error("Selected revision could not be imported");
        }

        dispatch(
          specsEditorActions.hydrateModelLayers({
            layerRuntimeByKey: {},
            layerRuntimeOrder: [],
          })
        );
        contextEditor.setSpecsModel(model);
        contextEditor.setImportSpecs(runtimeSpecs);
        contextEditor.setImportSelectedProduct(productName);
        contextEditor.setImportSelectedVersion(version.name);
        setLoadedRevisionId(response.data?.revisionId ?? revisionId);
        selectPositions(undefined);
        selectHistory();
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Failed to load revision"
        );
      } finally {
        setHistoryLoadingRevisionId(null);
      }
    },
    [
      contextEditor,
      dispatch,
      identity.productName,
      identity.productVersion,
      selectHistory,
      selectPositions,
    ]
  );

  const fetchHistoryRevisionRecord = React.useCallback(
    async (
      productName: string,
      revisionId: string
    ): Promise<SpecsRevisionRecord> => {
      const cachedRevision = historyRevisionCache[revisionId];
      if (cachedRevision) {
        return cachedRevision;
      }

      const response = await SpecsRevisionService.getSpecRevision(
        "product",
        productName,
        revisionId
      );
      const revision = response.data;
      setHistoryRevisionCache((current) => ({
        ...current,
        [revisionId]: revision,
      }));
      return revision;
    },
    [historyRevisionCache]
  );

  const handleExpandHistoryRevision = React.useCallback(
    async (
      revision: SpecsHistoryRevision,
      fallbackRevisionId: string | null,
      compareAgainstLegacySpecs: boolean
    ) => {
      const existingDiff = historyRevisionDiffs[revision.revisionId];
      if (
        existingDiff?.status === "loading" ||
        existingDiff?.status === "ready"
      ) {
        return;
      }

      const compareRevisionId = revision.sourceRevisionId ?? fallbackRevisionId;

      if (!compareAgainstLegacySpecs && !compareRevisionId) {
        setHistoryRevisionDiffs((current) => ({
          ...current,
          [revision.revisionId]: {
            status: "ready",
            text: "No source revision to compare",
          },
        }));
        return;
      }

      const productName = identity.productName;
      if (!productName) {
        setHistoryRevisionDiffs((current) => ({
          ...current,
          [revision.revisionId]: {
            status: "error",
            error: "Select a product before loading a diff",
          },
        }));
        return;
      }

      setHistoryRevisionDiffs((current) => ({
        ...current,
        [revision.revisionId]: {
          status: "loading",
        },
      }));

      try {
        const currentRevision = await fetchHistoryRevisionRecord(
          productName,
          revision.revisionId
        );
        let sourceSnapshot: Record<string, any>;

        if (compareAgainstLegacySpecs) {
          const legacySnapshotKey = `${productName}#${identity.productVersion}`;
          const cachedLegacySnapshot =
            historyLegacySnapshotCache[legacySnapshotKey];

          if (cachedLegacySnapshot) {
            sourceSnapshot = cachedLegacySnapshot;
          } else {
            const publishedResponse =
              await SpecsRevisionService.getPublishedSpecsCatalog();
            const publishedSpecs = publishedResponse.data;
            const publishedProduct = publishedSpecs?.products?.find(
              (product: any) => product?.name === productName
            );

            if (!publishedProduct) {
              throw new Error("Failed to load published legacy specs");
            }

            sourceSnapshot = publishedProduct;
            setHistoryLegacySnapshotCache((current) => ({
              ...current,
              [legacySnapshotKey]: sourceSnapshot,
            }));
          }
        } else {
          if (!compareRevisionId) {
            throw new Error("Missing revision to compare");
          }

          const sourceRevision = await fetchHistoryRevisionRecord(
            productName,
            compareRevisionId
          );
          sourceSnapshot = sourceRevision.json;
        }

        const sourceVersionSnapshot = (
          sourceSnapshot as ProductSnapshot
        ).versions.find(
          (candidate) => candidate.name === identity.productVersion
        );
        if (!sourceVersionSnapshot) {
          const sourceName = compareAgainstLegacySpecs
            ? "Base specs"
            : "Source revision";
          throw new Error(
            `${sourceName} product does not include version ${identity.productVersion}`
          );
        }

        const currentVersionSnapshot = (
          currentRevision.json as ProductSnapshot
        ).versions.find(
          (candidate) => candidate.name === identity.productVersion
        );
        if (!currentVersionSnapshot) {
          throw new Error(
            `Current revision product does not include version ${identity.productVersion}`
          );
        }

        setHistoryRevisionDiffs((current) => ({
          ...current,
          [revision.revisionId]: {
            status: "ready",
            text: formatSpecsRevisionDiff(
              sourceVersionSnapshot,
              currentVersionSnapshot
            ),
          },
        }));
      } catch (error) {
        setHistoryRevisionDiffs((current) => ({
          ...current,
          [revision.revisionId]: {
            status: "error",
            error:
              error instanceof Error ? error.message : "Failed to load diff",
          },
        }));
      }
    },
    [
      fetchHistoryRevisionRecord,
      historyLegacySnapshotCache,
      historyRevisionDiffs,
      identity.productName,
      identity.productVersion,
    ]
  );

  const handleSaveDraftRevision = React.useCallback(async () => {
    setHistorySaving(true);
    try {
      const result = await saveCurrentRevision({
        changeNote: "Saved from specs editor history",
        sourceRevisionId: loadedRevisionId,
      });
      setLoadedRevisionId(result.revisionId);
      setHistoryCursorStack([null]);
      setHistoryPageIndex(0);
      resolvedModals.showValidationResult({ valid: true, issues: [] });
      toast.success(`Saved revision ${result.revisionId.slice(0, 8)}`);
      void loadRevisionHistoryPage(null);
    } catch (error) {
      const validationResult = extractSpecsValidationResult(error);
      if (validationResult) {
        resolvedModals.showValidationResult(validationResult);
        toast.error("Validation failed. Fix the issues before saving.");
      } else {
        toast.error(
          error instanceof Error ? error.message : "Failed to save revision"
        );
      }
    } finally {
      setHistorySaving(false);
    }
  }, [
    loadRevisionHistoryPage,
    loadedRevisionId,
    resolvedModals,
    saveCurrentRevision,
  ]);

  const handleSaveAndPublishRevision = React.useCallback(async () => {
    setHistoryPublishing(true);
    try {
      const isValid = await runValidation();
      if (!isValid) {
        throw new Error("Validation failed. Fix the issues before saving.");
      }
      const result = await saveCurrentRevision({
        changeNote: "Saved and published from specs editor history",
        sourceRevisionId: loadedRevisionId,
      });
      await SpecsRevisionService.publishSpecRevision(
        "product",
        identity.productName,
        result.revisionId
      );
      setLoadedRevisionId(result.revisionId);
      setHistoryCursorStack([null]);
      setHistoryPageIndex(0);
      resolvedModals.showValidationResult({ valid: true, issues: [] });
      toast.success(`Published revision ${result.revisionId.slice(0, 8)}`);
      void loadRevisionHistoryPage(null);
    } catch (error) {
      const validationResult = extractSpecsValidationResult(error);
      if (validationResult) {
        resolvedModals.showValidationResult(validationResult);
        toast.error("Validation failed. Fix the issues before publishing.");
      } else {
        toast.error(
          error instanceof Error ? error.message : "Failed to publish revision"
        );
      }
    } finally {
      setHistoryPublishing(false);
    }
  }, [
    loadedRevisionId,
    loadRevisionHistoryPage,
    runValidation,
    saveCurrentRevision,
    identity.productName,
    resolvedModals,
  ]);

  const handlePublishHistoryRevision = React.useCallback(
    async (revisionId: string) => {
      if (!identity.productName) {
        toast.error("Select a product before publishing a revision");
        return;
      }

      setHistoryPublishingRevisionId(revisionId);
      try {
        const promoteResponse =
          await SpecsRevisionService.promoteRevisionToNext(
            "product",
            identity.productName,
            revisionId
          );
        toast.success(formatPromotionSuccessMessage(promoteResponse.data));
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : "Failed to promote revision to next env"
        );
      } finally {
        setHistoryPublishingRevisionId(null);
      }
    },
    [identity.productName]
  );

  const handleValidateRevision = React.useCallback(async () => {
    if (!identity.productName) {
      return;
    }

    setHistoryValidating(true);
    try {
      const saveResult = await saveCurrentRevision({
        changeNote: "Saved before validation",
        sourceRevisionId: loadedRevisionId,
      });
      setLoadedRevisionId(saveResult.revisionId);
      setHistoryCursorStack([null]);
      setHistoryPageIndex(0);
      void loadRevisionHistoryPage(null);

      const response = await SpecsRevisionService.validateSpecRevision(
        "product",
        identity.productName,
        saveResult.revisionId
      );
      resolvedModals.showValidationResult(response.data);
      const displayedIssues = filterDisplayedIssues(response.data.issues ?? []);
      if (displayedIssues.length > 0) {
        toast.warning("Validation completed with issues");
      } else {
        toast.success("Validation passed");
      }
    } catch (error) {
      const validationResult = extractSpecsValidationResult(error);
      if (validationResult) {
        resolvedModals.showValidationResult(validationResult);
        toast.error("Validation failed");
      } else {
        toast.error(
          error instanceof Error ? error.message : "Failed to validate revision"
        );
      }
    } finally {
      setHistoryValidating(false);
    }
  }, [
    loadedRevisionId,
    loadRevisionHistoryPage,
    identity.productName,
    resolvedModals,
    saveCurrentRevision,
  ]);

  // Reset all history-specific state when switching to a different product.
  React.useEffect(() => {
    setHistoryCursorStack([null]);
    setHistoryPageIndex(0);
    setHistoryNextCursor(null);
    setHistoryCurrentDraftRevisionId(null);
    setHistoryRevisions([]);
    setHistoryError(null);
    setLoadedRevisionId(null);
    setHistoryPublishingRevisionId(null);
    setHistoryRevisionDiffs({});
    setHistoryRevisionCache({});
    setHistoryLegacySnapshotCache({});
  }, [identity.productName]);

  React.useEffect(() => {
    if (
      loadedRevisionId ||
      !importLoadedRevision ||
      importLoadedRevision.productName !== identity.productName
    ) {
      return;
    }

    setLoadedRevisionId(importLoadedRevision.revisionId);
  }, [identity.productName, importLoadedRevision, loadedRevisionId]);

  // Fetch the current history page only while the history panel is active.
  React.useEffect(() => {
    if (selectedPanelKind !== "history") {
      return;
    }

    void loadRevisionHistoryPage(currentHistoryCursor);
  }, [currentHistoryCursor, loadRevisionHistoryPage, selectedPanelKind]);

  const builtRevisionHistory = {
    loadedRevisionId,
    historySaving,
    historyPublishing,
    historyValidating,
    historyPageProps: {
      productName: identity.productName,
      productVersion: identity.productVersion,
      revisions: historyRevisions,
      revisionDiffs: historyRevisionDiffs,
      loading: historyLoading,
      error: historyError,
      pageIndex: historyPageIndex,
      hasPreviousPage: historyPageIndex > 0,
      hasNextPage: !!historyNextCursor,
      loadedRevisionId,
      loadingRevisionId: historyLoadingRevisionId,
      publishingRevisionId: historyPublishingRevisionId,
      onPreviousPage: handleHistoryPreviousPage,
      onNextPage: handleHistoryNextPage,
      onLoadRevision: handleLoadHistoryRevision,
      onPublishRevision: handlePublishHistoryRevision,
      onExpandRevision: handleExpandHistoryRevision,
    },
    handleSaveDraftRevision,
    handleSaveAndPublishRevision,
    handleValidateRevision,
  } satisfies SpecsRevisionHistoryValue;

  if (args) {
    return builtRevisionHistory;
  }

  if (!contextRevisionHistory) {
    throw new Error(
      "useSpecsRevisionHistory must be used within a SpecsEditorContext provider"
    );
  }

  return contextRevisionHistory;
}
